import { AlertModal, Loader, Spinner } from '@/components/Common';
import { Input } from '@/components/ui';
import { CACHE_TIME } from '@/constants';
import { trpc } from '@/data-loaders';
import { useHandleError, useHandleSuccess } from '@/hooks';
import { useAwmsStore } from '@/store';
import { Button } from '@curtin-dxp/curtin-ui';
import { CircleCheckBig, UserX } from 'lucide-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

export const RemoveAcademics = () => {
  const [enteredAcademicId, setEnteredAcademicId] = useState<string>('');
  const [alert, setAlert] = useState<boolean>(false);
  const [validateIdButtonClicked, setValidateIdButtonClicked] = useState<number>(0);
  const [validId, setValidId] = useState<boolean>(false);
  const [userIdToValidate, setUserIdToValidate] = useState<string>('');
  const sendToastRemoveAcademicSuccess = useHandleSuccess({ title: 'Remove academic' });
  const activeYear = useAwmsStore((state) => state.activeYear);
  const handleError = useHandleError();
  const lastErrorRef = useRef<any>(null);

  const {
    data: academic,
    refetch,
    error: validateAcademicIdError,
  } = trpc.getAcademicById.useQuery(
    { academicId: userIdToValidate, year: activeYear },
    {
      cacheTime: CACHE_TIME,
      enabled: validId,
      retry: false,
    },
  );

  const { isLoading, mutate: removeAcademicByIdMutation } = trpc.removeAcademicById.useMutation({
    onSuccess: async () => {
      sendToastRemoveAcademicSuccess();
      setValidId(false);
    },
    onError: (error) => {
      handleError([error]);
    },
  });
  useEffect(() => {
    if (academic) {
      setValidId(true);
    }
  }, [academic]);

  useEffect(() => {
    if (validateAcademicIdError && lastErrorRef.current !== validateAcademicIdError) {
      setValidId(false);
      handleError([validateAcademicIdError]);
      lastErrorRef.current = validateAcademicIdError;
    }
  }, [handleError, validateAcademicIdError]);

  useEffect(() => {
    if (userIdToValidate) refetch();
  }, [userIdToValidate, validateIdButtonClicked, refetch]);

  const handleAcademicInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValidId(false);
    setUserIdToValidate('');
    setEnteredAcademicId(event.target.value);
  };

  const handleValidateAcademicId = () => {
    setValidateIdButtonClicked(validateIdButtonClicked + 1);
    setUserIdToValidate(enteredAcademicId);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <AlertModal
        title="Do you want to remove this academic?"
        description="This will permanently delete the academic and related data."
        confirmAction={() => {
          setAlert(false);
          if (validId && academic) {
            removeAcademicByIdMutation({
              academicId: academic.id,
              curtinId: academic.curtinId,
              year: activeYear,
            });
          }
        }}
        open={alert}
        cancelAction={() => {
          setAlert(false);
        }}
      />
      <div className="ml-8 mt-10">
        <div className="flex w-[400px] justify-between">
          <div className="relative">
            <Input
              type="text"
              value={enteredAcademicId}
              className="w-[180px] border-secondary-grey-04 pr-10 text-b2"
              onChange={handleAcademicInputChange}
              placeholder="Enter Curtin ID"
            />
            {validId && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">✔️</span>}
          </div>
          <Button
            variant="outline-secondary"
            className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
              focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
            aria-label="Validate Curtin ID"
            onClick={handleValidateAcademicId}
          >
            Validate ID
            <CircleCheckBig className="ml-[10px] h-6 w-6" />
          </Button>
        </div>
        {validId && (
          <div className="flex h-[150px] flex-col items-start justify-between">
            <div className="flex flex-col items-start justify-center gap-3">
              <div className="mt-4 flex justify-normal gap-3">
                <div className="text-base font-semibold leading-6 text-[#1A1A1A]">
                  {academic?.lastName}, {academic?.firstName}{' '}
                </div>
                <div className="ml-2 font-proxima text-sm font-normal leading-6">{academic?.curtinId}</div>
              </div>
              <div className="text-base font-normal leading-6">{academic?.school?.name}</div>
            </div>
            <Button
              variant="outline-secondary"
              className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
              aria-label="Validate academic ID"
              onClick={() => setAlert(true)}
            >
              Remove Academic
              <UserX className="ml-[12px] h-6 w-6" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
